import type { ThemeConfig } from 'antd';
import { palette } from './colors';

/**
 * shadcn/ui inspired theme (Zinc palette + black primary)
 * https://ui.shadcn.com/themes
 */
export const contipleTheme: ThemeConfig = {
  token: {
    // ── Seed Tokens ──
    colorPrimary: palette.neutral[950],
    colorSuccess: palette.emerald[500],
    colorWarning: palette.yellow[500],
    colorError: palette.red[500],
    colorInfo: palette.neutral[950],
    colorLink: palette.blue[500],
    colorTextBase: palette.neutral[950],
    colorBgBase: palette.white,
    fontFamily:
      "'Pretendard Variable', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif",
    fontSize: 14,
    borderRadius: 4,

    // ── Font Size ──
    fontSizeSM: 13,
    fontSizeLG: 14,
    fontSizeXL: 18,
    fontSizeHeading1: 36,
    fontSizeHeading2: 24,
    fontSizeHeading3: 18,
    fontSizeHeading4: 16,
    fontSizeHeading5: 15,
    fontWeightStrong: 600,

    // ── Line Height ──
    lineHeight: 1.5714,
    lineHeightSM: 1.5385,
    lineHeightLG: 1.5,
    lineHeightHeading1: 1.2222,
    lineHeightHeading2: 1.3333,
    lineHeightHeading3: 1.5556,
    lineHeightHeading4: 1.5,
    lineHeightHeading5: 1.4667,

    // ── Border Radius ──
    borderRadiusSM: 4,
    borderRadiusLG: 8,

    // ── Color / Primary ──
    colorPrimaryHover: palette.neutral[700],

    // ── Color / Text ──
    colorText: palette.neutral[950],
    colorTextSecondary: palette.neutral[500],
    colorTextTertiary: palette.neutral[400],
    colorTextQuaternary: palette.neutral[300],
    colorTextDisabled: palette.neutral[300],
    colorTextPlaceholder: palette.neutral[400],
    colorTextDescription: palette.neutral[500],
    colorTextLightSolid: palette.white,

    // ── Color / Icon ──
    colorIcon: palette.neutral[950],

    // ── Color / Background ──
    colorBgContainer: palette.white,
    colorBgLayout: palette.white,
    colorBgElevated: palette.white,
    colorBgSpotlight: palette.neutral[950],
    colorBgMask: 'rgba(0, 0, 0, 0.6)',
    colorBgContainerDisabled: palette.neutral[100],
    colorBgTextHover: 'rgba(9, 9, 11, 0.05)',

    // ── Color / Border ──
    colorBorder: palette.neutral[200],
    colorBorderSecondary: palette.neutral[150],
    colorSplit: palette.neutral[200],

    // ── Color / Fill ──
    colorFill: palette.neutral[300],
    colorFillSecondary: palette.neutral[150],
    colorFillTertiary: palette.neutral[100],
    colorFillQuaternary: palette.neutral[50],

    // ── Color / Success ──
    colorSuccessBg: palette.emerald[50],
    colorSuccessText: palette.emerald[600],
    colorSuccessBorder: palette.emerald[500],

    // ── Color / Warning ──
    colorWarningBg: palette.yellow[50],
    colorWarningText: palette.yellow[800],
    colorWarningBorder: palette.yellow[600],

    // ── Color / Error ──
    colorErrorBg: palette.red[50],
    colorErrorText: palette.red[500],
    colorErrorBorder: palette.red[300],

    // ── Color / Info ──
    colorInfoBg: palette.blue[50],
    colorInfoText: palette.blue[500],
    colorInfoBorder: palette.blue[400],
  },

  components: {
    // ── Alert ──
    Alert: {
      colorInfo: palette.blue[500],
      colorIcon: palette.neutral[500],
    },

    // ── Breadcrumb ──
    Breadcrumb: {
      itemColor: palette.neutral[500],
      lastItemColor: palette.neutral[950],
      linkColor: palette.neutral[500],
      linkHoverColor: palette.neutral[950],
      separatorColor: palette.neutral[400],
    },

    // ── Button ──
    Button: {
      primaryShadow: 'none',
      primaryColor: palette.white,
      defaultBg: palette.white,
      defaultBorderColor: palette.neutral[200],
      borderColorDisabled: palette.neutral[150],
      textHoverBg: 'rgba(9, 9, 11, 0.05)',
    },

    // ── Calendar ──
    Calendar: {
      itemActiveBg: 'rgba(9, 9, 11, 0.05)',
    },

    // ── Cascader ──
    Cascader: {
      optionSelectedBg: 'rgba(9, 9, 11, 0.05)',
    },

    // ── DatePicker ──
    DatePicker: {
      activeBg: palette.white,
      activeBorderColor: palette.neutral[950],
      hoverBorderColor: palette.neutral[200],
      hoverBg: palette.white,
      cellActiveWithRangeBg: 'rgba(0, 0, 0, 0.06)',
      cellHoverBg: 'rgba(9, 9, 11, 0.05)',
    },

    // ── Input ──
    Input: {
      activeBg: palette.white,
      activeBorderColor: palette.neutral[950],
      hoverBg: palette.white,
      hoverBorderColor: palette.neutral[200],
    },

    // ── Menu ──
    Menu: {
      itemSelectedBg: 'rgba(9, 9, 11, 0.05)',
      itemHoverBg: 'rgba(9, 9, 11, 0.05)',
      subMenuItemBg: palette.neutral[100],
    },

    // ── Modal ──
    Modal: {
      headerBg: palette.white,
      contentBg: palette.white,
      footerBg: palette.white,
      titleColor: palette.neutral[950],
    },

    // ── Pagination ──
    Pagination: {
      itemActiveBg: palette.white,
      itemActiveBgDisabled: 'rgba(255, 255, 255, 0.15)',
      itemActiveColorDisabled: palette.neutral[300],
      colorBgTextHover: 'rgba(9, 9, 11, 0.05)',
      colorTextDisabled: palette.neutral[400],
    },

    // ── Rate ──
    Rate: {
      starColor: palette.yellow[400],
    },

    // ── Select ──
    Select: {
      selectorBg: palette.white,
      optionSelectedBg: 'rgba(9, 9, 11, 0.05)',
      optionSelectedColor: palette.neutral[950],
      optionActiveBg: 'rgba(9, 9, 11, 0.05)',
      activeBorderColor: palette.neutral[950],
      hoverBorderColor: palette.neutral[200],
    },

    // ── Switch ──
    Switch: {
      handleBg: palette.white,
    },

    // ── Table ──
    Table: {
      headerBg: palette.neutral[100],
      headerColor: palette.neutral[500],
      borderColor: palette.neutral[200],
      rowHoverBg: palette.white,
      rowSelectedBg: palette.white,
    },

    // ── Tabs ──
    Tabs: {
      cardBg: palette.neutral[100],
      inkBarColor: palette.neutral[950],
      itemColor: palette.neutral[500],
      itemSelectedColor: palette.neutral[950],
    },

    // ── Tag ──
    Tag: {
      defaultBg: palette.white,
    },

    // ── Transfer ──
    Transfer: {
      controlItemBgActive: 'rgba(9, 9, 11, 0.05)',
      controlItemBgActiveHover: 'rgba(9, 9, 11, 0.05)',
      controlItemBgHover: 'rgba(9, 9, 11, 0.05)',
    },
  },
};

/**
 * Contiple 커스텀 시맨틱 토큰 (antd 표준 토큰 외)
 */
export const contipleCustomTokens = {
  semantic: {
    colorBgContainerSecondary: '#fafafa',
    colorBgContainerTertiary: '#f4f4f5',
    colorBgContainerQuaternary: '#e4e4e7',
    colorBgContainerSelected: '#eff6ff',
    colorBgTextHover: 'rgba(9, 9, 11, 0.05)',
    colorBgTextSelected: 'rgba(9, 9, 11, 0.05)',
    colorBgTextSelectedStrong: '#d4d4d8',
    colorTextAccent: '#2b7fff',
    colorIconSecondary: '#71717b',
    colorIconTertiary: '#9f9fa9',
    colorIconLight: '#ffffff',
    colorIconAccent: '#2b7fff',
    colorIconDisabled: '#d4d4d8',
    colorBorderStrong: '#d4d4d8',
    colorBorderDisabled: '#ececee',
    colorInfoBorderSecondary: '#dbeafe',
    colorErrorBorderSecondary: '#ffe2e2',
  },
  gnb: {
    colorGnbAdminBg: '#09090b',
    colorGnbCounselBg: '#162456',
    colorGnbCounselIcon: '#2b7fff',
    colorGnbVocBg: '#002c22',
    colorGnbVocIcon: '#00bc7d',
    colorGnbIssueBg: '#4d0218',
    colorGnbIssueIcon: '#ff6467',
    colorGnbSelectBg: 'rgba(255, 255, 255, 0.2)',
    colorGnbSelectHoverBg: 'rgba(255, 255, 255, 0.3)',
  },
  avatar: {
    colorAvatarDefaultBg: '#71717b',
    colorAvatarOrangeBg: '#f54900',
    colorAvatarPinkBg: '#fb64b6',
    colorAvatarTealBg: '#00bba7',
    colorAvatarDarkBg: '#09090b',
    colorAvatarBorder: '#d4d4d8',
  },
  badge: {
    colorBadgeOrangeBg: '#ffedd4',
    colorBadgeOrangeText: '#f54900',
    colorBadgeGreyBg: '#e4e4e7',
    colorBadgeGreyText: '#71717b',
    colorBadgePurpleBg: '#f3e8ff',
    colorBadgePurpleText: '#9810fa',
    colorBadgeSkyBg: '#dff2fe',
    colorBadgeSkyText: '#0084d1',
    colorBadgeGreenBg: '#f0fdf4',
    colorBadgeGreenText: '#008236',
    colorBadgeIndigoBg: '#eef2ff',
    colorBadgeIndigoText: '#4f39f6',
    colorBadgeAmberBg: '#fffbeb',
    colorBadgeAmberText: '#e17100',
    colorBadgeRedBg: '#fef2f2',
    colorBadgeRedText: '#fb2c36',
    colorBadgeNeutralBg: '#ffffff',
    colorBadgeNeutralText: '#71717b',
    colorBadgeNeutralBorder: '#e4e4e7',
  },
  filetype: {
    colorFileImageBg: '#1447e6',
    colorFileVideoBg: '#ec003f',
    colorFileExcelBg: '#005f5a',
    colorFilePdfBg: '#e7000b',
    colorFileGifBg: '#ff6900',
    colorFilePptBg: '#ca3500',
    colorFileMp4Bg: '#f6339a',
    colorFilePsBg: '#0f172b',
    colorFilePsIcon: '#51a2ff',
    colorFileHwpBg: '#0084d1',
    colorFileTxtBg: '#71717b',
    colorFileZipBg: '#71717b',
    colorFileAiBg: '#461901',
    colorFileAiIcon: '#ffb900',
    colorFileIconInvert: '#ffffff',
  },
  chart: {
    colorChartBlue: '#2b7fff',
    colorChartPurple: '#9810fa',
    colorChartYellow: '#f0b100',
    colorChartGrid: '#e4e4e7',
  },
} as const;

export type ContipleCustomTokens = typeof contipleCustomTokens;
